import React, {useRef, useEffect} from "react";
import {CheckOutlined, PhoneOutlined, RollbackOutlined} from "@ant-design/icons";
import {Button, Input, Tooltip, type InputRef, Form} from "antd";
import {StepRow} from "@/components/Modals/LoginOrRegistrationModal/StepRow";
import {FormFieldRow} from "@/components/Modals/LoginOrRegistrationModal/FormFieldRow";
import {ActionButton} from "@/components/Modals/LoginOrRegistrationModal/ActionButton";
import {LoadingIcon} from "@/components/Modals/LoginOrRegistrationModal/LoadingIcon";

const { OTP } = Input;

type OtpStepProps = {
    currentMode: 'login' | 'register';
    otpSent: boolean;
    otpSendInProgress: boolean;
    authSendInProgress: boolean;
    otpCooldown: number;
    phoneValue?: string;
    enteredOtp?: string;
    onSendOtp: () => Promise<void>;
    onResendOtp: () => Promise<void>;
    onSubmitOtp: () => void;
    onOtpChange: (value?: string) => void;
    onBackToPhoneEdit: () => void;
    handlePhoneChanged: (newValue?: string) => void;
};
export const OtpStep: React.FC<OtpStepProps> = (props) => {
    const {
        currentMode,
        otpSent,
        otpSendInProgress,
        authSendInProgress,
        otpCooldown,
        phoneValue,
        enteredOtp,
        onSendOtp,
        onResendOtp,
        onSubmitOtp,
        onOtpChange,
        onBackToPhoneEdit,
        handlePhoneChanged
    } = props;
    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldValue("phone", phoneValue)
    }, []);

    // @ts-ignore
    const handleFormValuesChange: FormProps['onValuesChange'] = (changedValues, allValues) => {
        const [fieldName, fieldValue] = Object.entries(changedValues)[0] || [];
        handlePhoneChanged(`${fieldValue}`);
    };
    
    const renderPhoneForm = () => {
        return (
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onValuesChange={handleFormValuesChange}
                className="w-full"
                disabled={otpSent || authSendInProgress}
            >
                <FormFieldRow label="Мобільний номер">
                    <Form.Item name="phone" style={{height: '32px', borderRadius: 0, margin: 0}}
                               initialValue="+380" // Початкове значення з префіксом
                               rules={[
                                   { required: true, message: "Поле обов'язкове" },
                                   { pattern: /^\+380\d{9}$/, message: "Формат: +380XXXXXXXXX" }
                               ]}
                               normalize={(value) => {
                                   const prefix = '+380';
                                   // Якщо користувач намагається видалити префікс, повертаємо його назад
                                   if (!value || !value.startsWith(prefix)) {
                                       return prefix;
                                   }
                                   // Дозволяємо вводити лише цифри після префікса (разом не більше 13 символів)
                                   const digitsOnly = value.slice(prefix.length).replace(/\D/g, '');
                                   return `${prefix}${digitsOnly}`.slice(0, 13);
                               }}
                    >
                        <Input
                            onKeyDown={(e) => {
                                // Забороняємо бекспейс, якщо курсор стоїть одразу після префікса
                                if (
                                    e.key === 'Backspace' &&
                                    (e.target as HTMLInputElement).selectionStart! <= 4 &&
                                    (e.target as HTMLInputElement).selectionEnd! <= 4
                                ) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </Form.Item>
                </FormFieldRow>
            </Form>
        )
    }
    
    if (!otpSent) {
        return (
            <StepRow
                step={currentMode === 'register' ? 2 : 1}
                title="Введіть Ваш номер телефону та отримайте SMS-повідомлення з ОТР-паролем"
            >
                <div className="flex w-full flex-col gap-7">
                    {renderPhoneForm()}

                    <div className="flex justify-center my-6">
                        <ActionButton
                            type="primary"
                            onClick={onSendOtp}
                            disabled={otpSendInProgress || otpCooldown > 0}
                            icon={otpSendInProgress ? <LoadingIcon/> : <PhoneOutlined/>}
                        >
                            {otpCooldown > 0 ? `Отримати ОТР-пароль (${otpCooldown})` : 'Отримати ОТР-пароль'}
                        </ActionButton>
                    </div>
                </div>
            </StepRow>
        );
    }

    return (
        <>
            <StepRow
                step={currentMode === 'register' ? 2 : 1}
                title="Введіть Ваш номер телефону та отримайте SMS-повідомлення з ОТР-паролем"
            >
                <div className="flex w-full flex-col gap-4">
                    {renderPhoneForm()}

                    <div className="flex items-center gap-3 md:pl-[196px]">
                        <span className="text-base text-[#8c8c8c]">ОТР-пароль надіслано</span>
                        <Tooltip title="Повернутись до введення номеру телефону">
                            <Button
                                type="text"
                                icon={<RollbackOutlined/>}
                                onClick={onBackToPhoneEdit}
                                disabled={authSendInProgress || otpSendInProgress}
                            />
                        </Tooltip>
                    </div>
                </div>
            </StepRow>

            <StepRow
                step={currentMode === 'register' ? 3 : 2}
                title="Введіть ОТР-пароль, який був надісланий на Ваш номер телефону"
            >
                <div className="flex flex-col items-center gap-7">
                    <OTP
                        autoFocus
                        length={4}
                        value={enteredOtp}
                        onChange={onOtpChange}
                        disabled={authSendInProgress || otpSendInProgress}
                        formatter={(value) => value.toUpperCase()}
                        style={{width: '200px'}}
                        separator=" "
                    />

                    <Button
                        type="link"
                        onClick={onResendOtp}
                        disabled={otpCooldown > 0 || authSendInProgress || otpSendInProgress}
                        className="h-auto p-0 text-base"
                    >
                        {otpCooldown > 0 ? `Повторно отримати ОТР-пароль (${otpCooldown})` : 'Повторно отримати ОТР-пароль'}
                    </Button>
                    <ActionButton
                        type="primary"
                        onClick={onSubmitOtp}
                        disabled={authSendInProgress || otpSendInProgress || !enteredOtp || enteredOtp.length !== 4}
                        icon={authSendInProgress ? <LoadingIcon/> : <CheckOutlined/>}
                    >
                        {currentMode === 'register' ? 'Зареєструватись' : 'Авторизуватись'}
                    </ActionButton>
                </div>
            </StepRow>
        </>
    );
};